// One delivery (M6): what arrived, line by line, against what was ordered (ordered, arrived so
// far, still to come), into which lot, with the supplier's lot and best-before and the cost
// it went into stock at; what it is worth at the order's prices, to check against the
// supplier's invoice; every flag explained with what to do and a way to do it (D-060:
// flagged, never refused). A lot that arrived past its best-before says how it went in: as
// expired (D-068), or, without an expiry rule, as usable stock nothing blocks (UI6A-2), in one
// warning. While another delivery against the same order is held for a manager, a short line
// says the rest may be in it: book that first, never "close the order short" (UI6A-102).

import type { PoDetail, ReceiptDetail } from '@enp/domain/api';
import { isQtyString } from '@enp/domain/quantity';
import { Badge, Button, Money, PageHeader, Skeleton, unitText } from '@enp/ui';
import { useInfiniteQuery, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, PackageCheck, SquareX, TriangleAlert } from 'lucide-react';
import { meQueryOptions } from '../../../../auth/session.js';
import {
  baseQtyText,
  countedInPacks,
  countedText,
  deliveryValue,
  estimateWithoutOrder,
  pastBestBeforeButUsable,
  receivedAgainst,
} from '../../../../purchasing/figures.js';
import {
  BaseQty,
  FlagList,
  KeyHints,
  PurchasingBreadcrumb,
  Section,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import {
  heldDeliveriesQuery,
  orderQuery,
  ordersQuery,
  receiptQuery,
  receiptsQuery,
  supplierQuery,
} from '../../../../purchasing/queries.js';
import {
  expiredOnArrivalWords,
  type FlagExplanation,
  heldShortWords,
} from '../../../../purchasing/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { BestBefore, StockStateBadge } from '../../../../stock/components.js';
import { calendarToday, NO_STATE_KEY, plainDateLabel } from '../../../../stock/labels.js';
import { Fact, Facts } from '../../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/purchasing/receipts/$receiptId')({
  component: ReceiptPage,
});

function allFlags(r: ReceiptDetail): number {
  return r.flags.length + r.lines.reduce((n, l) => n + l.flags.length, 0);
}

const actionLink =
  'inline-flex items-center gap-1 rounded-sm text-sm font-medium text-primary-text underline underline-offset-2';

/** Links that act on a flag where the fix is (UI6A-10): the order to close, the lot to check. */
function flagAction(
  code: string,
  r: ReceiptDetail,
  po: PoDetail | undefined,
  held: readonly { hold_id: string; number: string | null }[],
  lot?: { productId: string; lotId: string; lotCode: string },
) {
  const short = code === 'under_received' || code === 'not_delivered';
  // The rest may be in a delivery held for a manager: book that, don't close short (UI6A-102).
  const first = held[0];
  if (short && first) {
    return (
      <Link
        to="/settings/held-actions/$holdId"
        params={{ holdId: first.hold_id }}
        search={r.po ? { po: r.po.id, po_number: r.po.number } : {}}
        className={actionLink}
      >
        Book {first.number ?? 'the held delivery'} in held actions
        <ArrowRight aria-hidden className="size-4" />
      </Link>
    );
  }
  if (short && r.po && po?.actions.close) {
    return (
      <Link
        to="/purchasing/orders/$orderId"
        params={{ orderId: r.po.id }}
        search={{ act: 'close' }}
        className={actionLink}
      >
        <SquareX aria-hidden className="size-4" />
        Close {r.po.number} short…
      </Link>
    );
  }
  if ((code === 'expired_on_arrival' || code === 'missing_best_before') && lot) {
    return (
      <Link
        to="/stock/movements"
        search={{ product: lot.productId, lot: lot.lotId }}
        className={actionLink}
      >
        See lot {lot.lotCode}
      </Link>
    );
  }
  if ((code === 'over_received' || code === 'unexpected_item') && r.po) {
    return (
      <Link to="/purchasing/orders/$orderId" params={{ orderId: r.po.id }} className={actionLink}>
        Open {r.po.number}
      </Link>
    );
  }
  return null;
}

/**
 * The state a line went into (UI6A-105). The default state of a product without an expiry
 * rule is usable stock: say so, and offer to set a rule, never "No expiry state" (which reads
 * as "this doesn't expire").
 */
function IntoState({ state }: { state: string }) {
  if (state !== NO_STATE_KEY) return <StockStateBadge state={state} />;
  return (
    <Badge tone="neutral" data-state={state} data-testid="usable-no-rule">
      Usable (no expiry rule set)
    </Badge>
  );
}

/** A quantity a line's flag carries, in words ("6 L"), or null. */
function flagQty(line: ReceiptDetail['lines'][number], code: string, key: string): string | null {
  const v = line.flags.find((f) => f.code === code)?.data?.[key];
  return typeof v === 'string' && isQtyString(v) ? baseQtyText(v, line.product.base_uom) : null;
}

/** The loading state in the loaded page's shape: header, the delivery's facts, its lines. */
function ReceiptSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <h1 className="sr-only">Delivery</h1>
      <span role="status" className="sr-only">
        Loading the delivery…
      </span>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5">
        <Skeleton className="h-5 w-24" />
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 5 }, (_, i) => (
            <span key={i} className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-40" />
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-raised p-5">
        <Skeleton className="h-5 w-20" />
        {[0, 1].map((i) => (
          <div key={i} className="flex flex-col gap-3 rounded-md border border-border p-4">
            <div className="flex flex-wrap justify-between gap-3">
              <span className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-56" />
              </span>
              <span className="grid grid-cols-4 gap-6">
                {[0, 1, 2, 3].map((c) => (
                  <Skeleton key={c} className="h-8 w-20" />
                ))}
              </span>
            </div>
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ReceiptPage() {
  const { receiptId } = Route.useParams();
  const navigate = useNavigate();
  const q = useQuery(receiptQuery(receiptId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  // Best-before judgements (days left, near expiry) use the calendar date, not the trading day.
  const today = calendarToday(me.account.timezone);
  const r = q.data;
  const order = useQuery({ ...orderQuery(r?.po?.id ?? ''), enabled: Boolean(r?.po) });
  const po = order.data;
  // Without an order (UI6A-112): a rough value at the supplier's prices today, and the
  // supplier's open orders it may belong to.
  const withoutOrder = Boolean(r && !r.po && r.supplier);
  const supplier = useQuery({
    ...supplierQuery(r?.supplier?.id ?? ''),
    enabled: withoutOrder,
  });
  const openOrders = useInfiniteQuery({
    ...ordersQuery({ supplier: r?.supplier?.id, view: 'expected' }),
    enabled: withoutOrder,
  });
  const candidates = openOrders.data?.pages.flatMap((p) => p.items) ?? [];
  // Deliveries against the same order held for a manager (UI6A-102): the rest may be there.
  const heldQ = useQuery({ ...heldDeliveriesQuery(r?.po?.id), enabled: Boolean(r?.po) });
  const held = (heldQ.data?.items ?? []).filter((h) => h.po !== null && h.po.id === r?.po?.id);
  const heldNumbers = held.map((h) => h.number ?? 'a held delivery');
  // The next delivery with something to check, for "j" (UI6A-18).
  const flaggedList = useInfiniteQuery(receiptsQuery({ flagged: true }));
  const flaggedIds = (flaggedList.data?.pages.flatMap((p) => p.items) ?? []).map((x) => x.id);
  const nextFlagged = flaggedIds
    .slice(flaggedIds.indexOf(receiptId) + 1)
    .find((id) => id !== receiptId);
  usePageKeys({
    b: () => void navigate({ to: '/purchasing/receipts' }),
    j: nextFlagged
      ? () =>
          void navigate({
            to: '/purchasing/receipts/$receiptId',
            params: { receiptId: nextFlagged },
          })
      : undefined,
  });
  useDocumentTitle(r ? r.number : 'Delivery');

  if (q.isError && !r) {
    return (
      <>
        <PurchasingBreadcrumb
          trail={[{ to: '/purchasing/receipts', label: 'Deliveries' }]}
          page="Delivery"
        />
        <div className="mt-4 max-w-2xl">
          <ErrorPanel
            error={q.error}
            titleAs="h1"
            what="This delivery"
            onRetry={() => void q.refetch()}
            extraAction={
              <Button variant="secondary" asChild>
                <Link to="/purchasing/receipts">Back to deliveries</Link>
              </Button>
            }
          />
        </div>
      </>
    );
  }
  if (!r) return <ReceiptSkeleton />;
  const flagged = allFlags(r);
  const value = po ? deliveryValue(r, po) : null;
  const lateBy =
    po?.expected_date && po.expected_date < r.trading_day ? { expected: po.expected_date } : null;
  const estimate =
    withoutOrder && supplier.data ? estimateWithoutOrder(r.lines, supplier.data.items) : null;
  /** The page's words for a flag, where it knows more than the flag (held, the lot's state). */
  const explainHere =
    (line?: ReceiptDetail['lines'][number]) =>
    (code: string): FlagExplanation | undefined => {
      if (code === 'expired_on_arrival' && line) {
        return expiredOnArrivalWords(pastBestBeforeButUsable(line));
      }
      if ((code === 'under_received' || code === 'not_delivered') && heldNumbers.length > 0) {
        return heldShortWords(
          code,
          heldNumbers,
          line ? flagQty(line, 'under_received', 'outstanding') : null,
        );
      }
      return undefined;
    };
  const hints: [string, string][] = [
    ['b', 'back to deliveries'],
    ...(nextFlagged ? ([['j', 'next to check']] as [string, string][]) : []),
  ];

  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[{ to: '/purchasing/receipts', label: 'Deliveries' }]}
            page={r.number}
          />
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>
              Delivery <span className="whitespace-nowrap font-mono">{r.number}</span>
            </span>
            {flagged > 0 ? (
              <Badge tone="warning" className="font-body">
                <TriangleAlert aria-hidden />
                {flagged} {flagged === 1 ? 'thing' : 'things'} to check
              </Badge>
            ) : (
              <Badge tone="success" className="font-body">
                <PackageCheck aria-hidden />
                Nothing flagged
              </Badge>
            )}
          </span>
        }
        description={`${r.supplier?.name ?? 'Supplier not recorded'} · received ${r.branch.name}, ${r.location.name}`}
        actions={<KeyHints keys={hints} />}
      />
      <div className="flex flex-col gap-5">
        <Section id="receipt-facts" title="Delivery">
          <Facts>
            <Fact label="Received">
              <AccountDate value={r.received_at} withTime /> by{' '}
              {r.received_by?.name ?? 'someone at a station'}
            </Fact>
            <Fact label="Against order">
              {r.po ? (
                <>
                  <Link
                    to="/purchasing/orders/$orderId"
                    params={{ orderId: r.po.id }}
                    className="whitespace-nowrap rounded-sm font-mono text-primary-text underline-offset-2 hover:underline"
                  >
                    {r.po.number}
                  </Link>
                  {lateBy && (
                    <span className="ml-2 font-medium text-state-danger" data-testid="arrived-late">
                      arrived after the expected {plainDateLabel(lateBy.expected, today)}
                    </span>
                  )}
                </>
              ) : (
                <span className="flex flex-col gap-1">
                  <span>None: received without an order</span>
                  {candidates.length > 0 && (
                    <span className="text-sm text-muted" data-testid="maybe-orders">
                      {r.supplier?.name} has {candidates.length} open{' '}
                      {candidates.length === 1 ? 'order' : 'orders'} it may belong to:{' '}
                      {candidates.map((c, i) => (
                        <span key={c.id}>
                          {i > 0 && ', '}
                          <Link
                            to="/purchasing/orders/$orderId"
                            params={{ orderId: c.id }}
                            className="whitespace-nowrap rounded-sm font-mono text-primary-text underline-offset-2 hover:underline"
                          >
                            {c.number}
                          </Link>
                          {c.expected_date &&
                            ` (expected ${plainDateLabel(c.expected_date, today)})`}
                        </span>
                      ))}
                      . Check with whoever received it.
                    </span>
                  )}
                </span>
              )}
            </Fact>
            <Fact label="Their delivery note">{r.supplier_doc_ref ?? 'Not recorded'}</Fact>
            <Fact label={r.po ? 'Worth at order prices' : 'Worth, roughly'}>
              {!r.po ? (
                estimate && estimate.priced > 0 ? (
                  <span data-testid="delivery-estimate">
                    About <Money cents={estimate.cents} className="font-semibold" /> ex VAT at
                    today’s price list
                    {estimate.unpriced > 0 &&
                      ` + ${estimate.unpriced} ${estimate.unpriced === 1 ? 'line' : 'lines'} without a price`}
                    <span className="whitespace-nowrap text-muted">
                      {' '}
                      · check it against their invoice
                    </span>
                  </span>
                ) : withoutOrder && supplier.isPending ? (
                  <Skeleton className="inline-block h-4 w-24" />
                ) : (
                  'No order or price list to price it from'
                )
              ) : order.isError ? (
                'The order didn’t load'
              ) : !value ? (
                <Skeleton className="inline-block h-4 w-24" />
              ) : (
                <span data-testid="delivery-value">
                  <Money cents={value.cents} className="font-semibold" /> ex VAT
                  {value.unvalued > 0 &&
                    ` + ${value.unvalued} ${value.unvalued === 1 ? 'line' : 'lines'} without a price`}
                  <span className="whitespace-nowrap text-muted">
                    {' '}
                    · check it against their invoice
                  </span>
                </span>
              )}
            </Fact>
            <Fact label="Note">{r.note ?? 'None'}</Fact>
          </Facts>
          {r.flags.length > 0 && (
            <FlagList
              flags={r.flags}
              label="About the whole delivery"
              action={(code) => flagAction(code, r, po, held)}
              explain={explainHere()}
            />
          )}
        </Section>

        <Section id="receipt-lines" title={`Lines (${r.lines.length})`}>
          <ul className="flex flex-col gap-3">
            {r.lines.map((l) => {
              const unit = l.product.base_uom;
              const against = receivedAgainst(l, po);
              // "Set an expiry rule" only for a product that records a best-before.
              const recordsBestBefore =
                l.best_before !== null || l.flags.some((f) => f.code === 'missing_best_before');
              return (
                <li
                  key={l.id}
                  data-line={l.product.id}
                  className="flex flex-col gap-3 rounded-md border border-border bg-surface p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
                    <span className="flex flex-col leading-tight">
                      <span className="font-medium text-fg">{l.product.name}</span>
                      <span className="text-sm text-muted" data-testid="counted">
                        {(() => {
                          // Packs first, like every other line (UI6A-114): "Counted 2 cases =
                          // 100 items", however the station counted it.
                          const pl = po?.lines.find((x) => x.id === l.po_line_id);
                          const inPacks = countedInPacks(l, pl);
                          if (inPacks)
                            return `Counted ${inPacks} = ${countedText(l.qty, l.uom, unit)}`;
                          return (
                            <>
                              Counted {countedText(l.qty, l.uom, unit)}
                              {l.base_qty !== null && l.uom !== unit && (
                                <>
                                  {' '}
                                  = <BaseQty value={l.base_qty} unit={unit} />
                                </>
                              )}
                            </>
                          );
                        })()}
                        {l.base_qty === null && ' · not booked into stock'}
                      </span>
                    </span>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
                      <div className="flex flex-col">
                        <dt className="text-muted">Lot</dt>
                        <dd>
                          <Link
                            to="/stock/movements"
                            search={{ product: l.product.id, lot: l.lot.id }}
                            className="whitespace-nowrap rounded-sm font-mono text-primary-text underline-offset-2 hover:underline"
                          >
                            {l.lot.lot_code}
                          </Link>
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-muted">Best-before</dt>
                        <dd>
                          <BestBefore date={l.best_before} today={today} />
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-muted">Their lot</dt>
                        <dd className="text-fg">{l.supplier_lot_ref ?? 'Not recorded'}</dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-muted">Cost per {unitText(unit, '1') || 'item'}</dt>
                        <dd>
                          {l.unit_cost_cents === null ? (
                            <span className="text-muted">No cost</span>
                          ) : (
                            <Money cents={l.unit_cost_cents} className="text-fg" />
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  {r.po && (
                    <dl
                      className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-md bg-surface-sunken px-3 py-2 text-sm sm:grid-cols-4"
                      aria-label={`${l.product.name} against the order`}
                      data-testid="against-order"
                    >
                      {against ? (
                        <>
                          <div className="flex flex-col">
                            <dt className="text-muted">Ordered</dt>
                            <dd className="font-medium text-fg">
                              <BaseQty value={against.ordered} unit={unit} />
                            </dd>
                          </div>
                          <div className="flex flex-col">
                            <dt className="text-muted">Arrived so far</dt>
                            <dd className="font-medium text-fg">
                              <BaseQty value={against.arrivedSoFar} unit={unit} />
                            </dd>
                          </div>
                          <div className="flex flex-col">
                            <dt className="text-muted">
                              {against.over ? 'Over by' : 'Still to come'}
                            </dt>
                            <dd
                              className={
                                against.over
                                  ? 'font-medium text-state-warning'
                                  : 'font-medium text-fg'
                              }
                            >
                              <BaseQty
                                value={against.over ? against.overBy : against.toCome}
                                unit={unit}
                              />
                            </dd>
                          </div>
                          <div className="flex flex-col">
                            <dt className="text-muted">This delivery, ex VAT</dt>
                            <dd className="text-fg">
                              {against.valueCents === null ? (
                                'No price'
                              ) : (
                                <Money cents={against.valueCents} />
                              )}
                            </dd>
                          </div>
                        </>
                      ) : (
                        <div className="col-span-full flex flex-col">
                          <dt className="sr-only">Order line</dt>
                          <dd className="text-muted">
                            {order.isPending ? (
                              <Skeleton className="h-4 w-2/3" />
                            ) : (
                              `Not a line of ${r.po.number}.`
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                  <span
                    className="flex flex-wrap items-center gap-2 text-sm text-muted"
                    data-testid="went-into-stock"
                  >
                    Into stock as <IntoState state={l.state} /> at {l.location.name}
                    {l.state === NO_STATE_KEY && recordsBestBefore && (
                      <>
                        <span aria-hidden>·</span>
                        <Link
                          to="/catalogue/$productId"
                          params={{ productId: l.product.id }}
                          className={actionLink}
                        >
                          Set an expiry rule
                        </Link>
                      </>
                    )}
                  </span>
                  <FlagList
                    explain={explainHere(l)}
                    flags={l.flags}
                    unit={unit}
                    label={`${l.product.name}: to check`}
                    action={(code) =>
                      flagAction(code, r, po, held, {
                        productId: l.product.id,
                        lotId: l.lot.id,
                        lotCode: l.lot.lot_code,
                      })
                    }
                  />
                </li>
              );
            })}
          </ul>
        </Section>
      </div>
    </>
  );
}
